/**
 * `<BoardViewTabs>`: the tracker's layout switcher, one button that names the
 * layout on screen and opens a menu of layouts. A viewer sees the enabled ones;
 * an editor sees the whole catalogue, each with a switch to turn it on or off
 * and, once on, a grip to reorder it. The active layout is the bold one.
 *
 * The FIRST enabled view is the one the tracker opens on (persisted in
 * `layout.views`, shared by the workspace). `table` is pinned: always present,
 * never removable.
 */
import { useRef } from 'react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  Switch,
} from '@monksflow/monks-ui';
import {
  CalendarRange,
  LayoutDashboard,
  SquareKanban,
  Table2,
  type LucideIcon,
} from 'lucide-react';

import type { TrackerViewType } from '@/entities/tracker';
import { useSortableList } from '@/shared/hooks';
import { DragHandle } from '@/shared/ui';
import { useTranslation } from 'react-i18next';
import { useCanEditTracker } from '../../lib/tracker-access';
import type { ParseKeys } from 'i18next';

/** The pinned baseline view — always present, never removable. */
const PINNED_VIEW: TrackerViewType = 'table';

const VIEW_META: Record<
  TrackerViewType,
  { labelKey: ParseKeys<'trackers'>; descriptionKey: ParseKeys<'trackers'>; Icon: LucideIcon }
> = {
  table: {
    labelKey: 'trackersBoard.boardViewTabs.table',
    descriptionKey: 'trackersBoard.boardViewTabs.rowsGroupedBySection',
    Icon: Table2,
  },
  kanban: {
    labelKey: 'trackersBoard.boardViewTabs.kanban',
    descriptionKey: 'trackersBoard.boardViewTabs.cardsByStatusColumn',
    Icon: SquareKanban,
  },
  timeline: {
    labelKey: 'trackersBoard.boardViewTabs.timeline',
    descriptionKey: 'trackersBoard.boardViewTabs.itemsOnADateAxis',
    Icon: CalendarRange,
  },
  dashboard: {
    labelKey: 'trackersBoard.boardViewTabs.dashboard',
    descriptionKey: 'trackersBoard.boardViewTabs.chartsAndRollUpMetrics',
    Icon: LayoutDashboard,
  },
};

/** Menu order. Tabs follow the user's saved order instead. */
const VIEW_CATALOGUE: TrackerViewType[] = ['table', 'kanban', 'dashboard', 'timeline'];

interface BoardViewTabsProps {
  /** Enabled views, in order — the first is the default-on-open. */
  views: TrackerViewType[];
  /**
   * Views this board can offer at all, defaulting to the whole catalogue. A
   * board whose numbers come from the server passes a shorter list: Dashboard
   * and Timeline chart the rows in hand, which on a paged board is a picture of
   * the first page presented as a picture of everything.
   */
  catalogue?: readonly TrackerViewType[];
  activeView: TrackerViewType;
  onSwitch: (view: TrackerViewType) => void;
  onAddView: (view: TrackerViewType) => void;
  onRemoveView: (view: TrackerViewType) => void;
  onReorder: (next: TrackerViewType[]) => void;
}

export function BoardViewTabs({
  views,
  catalogue = VIEW_CATALOGUE,
  activeView,
  onSwitch,
  onAddView,
  onRemoveView,
  onReorder,
}: BoardViewTabsProps) {
  const { t } = useTranslation('trackers');
  const canEdit = useCanEditTracker();
  const { labelKey, Icon } = VIEW_META[activeView];
  const activeLabel = t(labelKey);
  // Dropping a dragged row fires pointerup on whichever layout is under the
  // pointer, which Radix turns into a click; this keeps that from switching.
  const draggingRef = useRef(false);
  const { sensors, handleDragEnd } = useSortableList(views, ({ oldIndex, newIndex }) =>
    onReorder(arrayMove(views, oldIndex, newIndex)),
  );
  const move = (view: TrackerViewType, by: -1 | 1) => {
    const from = views.indexOf(view);
    const to = from + by;
    if (to < 0 || to >= views.length) return;
    onReorder(arrayMove(views, from, to));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" className="data-[state=open]:bg-accent">
          <Icon aria-hidden />
          <span className="text-muted-foreground">
            {t('trackersBoard.boardViewTabs.layoutPrefix')}
          </span>
          <span className="font-medium">{activeLabel}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-64"
        // A grip moved from the keyboard needs its arrows and Escape; the menu
        // would otherwise take them to move focus and to close.
        onKeyDown={(event) => {
          if (isGrip(event.target)) event.preventDefault();
        }}
        onEscapeKeyDown={(event) => {
          if (draggingRef.current) event.preventDefault();
        }}
      >
        <DropdownMenuLabel>{t('trackersBoard.boardViewTabs.layout')}</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={activeView}
          onValueChange={(next) => onSwitch(next as TrackerViewType)}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragStart={() => {
              draggingRef.current = true;
            }}
            onDragEnd={(event) => {
              draggingRef.current = false;
              handleDragEnd(event);
            }}
            onDragCancel={() => {
              draggingRef.current = false;
            }}
          >
            <SortableContext items={views} strategy={verticalListSortingStrategy}>
              <div>
                {views.map((view, index) => (
                  <LayoutRow
                    key={view}
                    view={view}
                    isDefault={index === 0}
                    canEdit={canEdit}
                    draggingRef={draggingRef}
                    onMove={move}
                    onRemoveView={onRemoveView}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </DropdownMenuRadioGroup>
        {canEdit
          ? VIEW_CATALOGUE.filter((view) => catalogue.includes(view) && !views.includes(view)).map(
              (view) => (
                <OffLayoutRow
                  key={view}
                  view={view}
                  draggingRef={draggingRef}
                  onAddView={onAddView}
                />
              ),
            )
          : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const isGrip = (target: EventTarget) =>
  target instanceof HTMLElement && target.getAttribute('aria-roledescription') === 'sortable';

interface LayoutRowProps {
  view: TrackerViewType;
  isDefault: boolean;
  canEdit: boolean;
  draggingRef: React.RefObject<boolean>;
  onMove: (view: TrackerViewType, by: -1 | 1) => void;
  onRemoveView: (view: TrackerViewType) => void;
}

/**
 * The menu keeps Tab inside it and moves arrows between items only, so a grip
 * is out of a keyboard user's reach; Alt+arrows on the row move it instead.
 */
const MOVE_KEYS: Partial<Record<string, -1 | 1>> = { ArrowUp: -1, ArrowDown: 1 };
// eslint-disable-next-line i18next/no-literal-string -- ARIA key names, not copy
const MOVE_SHORTCUTS = 'Alt+ArrowUp Alt+ArrowDown';

/** A drop fires pointerup on whatever row is under the pointer; this keeps it from acting. */
const ignoreWhileDragging =
  (draggingRef: React.RefObject<boolean>) => (event: React.PointerEvent) => {
    if (draggingRef.current) event.preventDefault();
  };

/** An enabled layout: pick it to switch to it; editors also get a grip and a switch. */
function LayoutRow({
  view,
  isDefault,
  canEdit,
  draggingRef,
  onMove,
  onRemoveView,
}: LayoutRowProps) {
  const { t } = useTranslation('trackers');
  const { labelKey, descriptionKey, Icon } = VIEW_META[view];
  const label = t(labelKey);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: view,
    disabled: !canEdit,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={`flex items-center ${isDragging ? 'relative z-10 opacity-60' : ''}`}
    >
      {canEdit ? (
        <DragHandle
          ariaLabel={t('trackersBoard.boardViewTabs.reorderLayout', { label })}
          title={t('trackersBoard.boardViewTabs.dragToReorder')}
          attributes={attributes}
          listeners={listeners}
          className="ml-1 p-0.5"
        />
      ) : null}
      <DropdownMenuRadioItem
        value={view}
        title={
          isDefault ? t('trackersBoard.boardViewTabs.opensByDefault', { label }) : t(descriptionKey)
        }
        onPointerUp={ignoreWhileDragging(draggingRef)}
        onKeyDown={(event) => {
          const by = MOVE_KEYS[event.key];
          if (!canEdit || !event.altKey || !by) return;
          // Also keeps the menu from moving focus to the next item.
          event.preventDefault();
          onMove(view, by);
        }}
        aria-keyshortcuts={canEdit ? MOVE_SHORTCUTS : undefined}
        // Bold marks the active layout, so Radix's dot and the room it keeps
        // on the left are dropped.
        className="flex-1 gap-2 pl-2 data-[state=checked]:font-semibold [&>span:first-child]:hidden"
      >
        <Icon className="size-4 shrink-0" aria-hidden />
        {label}
      </DropdownMenuRadioItem>
      {canEdit ? (
        <LayoutSwitch
          view={view}
          on
          onChange={() => onRemoveView(view)}
          locked={view === PINNED_VIEW}
          draggingRef={draggingRef}
        />
      ) : null}
    </div>
  );
}

/** A layout that is off: nothing to switch to yet, only its switch. */
function OffLayoutRow({
  view,
  draggingRef,
  onAddView,
}: {
  view: TrackerViewType;
  draggingRef: React.RefObject<boolean>;
  onAddView: (view: TrackerViewType) => void;
}) {
  const { t } = useTranslation('trackers');
  const { labelKey, descriptionKey, Icon } = VIEW_META[view];

  return (
    <div className="flex items-center">
      {/* Holds the grip's place, so labels line up with the enabled rows. */}
      <span className="ml-1 w-5 shrink-0" aria-hidden />
      <span
        title={t(descriptionKey)}
        className="flex flex-1 items-center gap-2 px-2 py-1.5 text-sm text-muted-foreground"
      >
        <Icon className="size-4 shrink-0" aria-hidden />
        {t(labelKey)}
      </span>
      <LayoutSwitch
        view={view}
        on={false}
        onChange={() => onAddView(view)}
        draggingRef={draggingRef}
      />
    </div>
  );
}

function LayoutSwitch({
  view,
  on,
  onChange,
  locked = false,
  draggingRef,
}: {
  view: TrackerViewType;
  on: boolean;
  onChange: () => void;
  locked?: boolean;
  draggingRef: React.RefObject<boolean>;
}) {
  const { t } = useTranslation('trackers');

  return (
    <DropdownMenuCheckboxItem
      checked={on}
      onCheckedChange={() => {
        if (!locked) onChange();
      }}
      // Turning several layouts on is one visit, not one per layout.
      onSelect={(e) => e.preventDefault()}
      onPointerUp={ignoreWhileDragging(draggingRef)}
      aria-label={t('trackersBoard.boardViewTabs.showLayout', {
        label: t(VIEW_META[view].labelKey),
      })}
      // `aria-disabled` rather than `disabled`: Radix drops a disabled item out
      // of the menu's arrow navigation, which would hide the pinned layout from
      // a keyboard or screen-reader user instead of telling them it is on and
      // cannot be turned off.
      aria-disabled={locked || undefined}
      className={`shrink-0 px-2 [&>span:first-child]:hidden ${locked ? 'cursor-default' : ''}`}
    >
      <Switch
        checked={on}
        disabled={locked}
        tabIndex={-1}
        aria-hidden
        className="pointer-events-none"
      />
    </DropdownMenuCheckboxItem>
  );
}
